import { useMutation, useQuery } from '@tanstack/react-query';
import { APITypes } from '../../primary-record-types';
import urls from '../../utils/urls';
import useNotify from '../useNotify';
import { getIntl } from '../../utils/intl';
import { appAPI } from '../../utils/api';
import {
    onCareTeamUpdate,
    getProfileCareTeamMembersCacheKey,
    getResourceRelatedCareTeamMembersCacheKey,
} from './cacheKeys';
import { useQueryErrorNotify } from '../useQueryCallbacks';
import { useHasPermission } from '../../components/v2BaseComponents/Permissions/hooks';

const intl = getIntl('useCareTeamMembers', {
    careTeamMembersLoadFailure: 'Failed to load care team members',
    createSuccess: 'Care team member created successfully',
    createFailure: 'Failed to create care team member',
    deleteSuccess: 'Care team member deleted successfully',
    deleteFailure: 'Failed to delete care team member',
    relatedToResourceFailure: 'Failed to load related care team members',
});

export type UseCareTeamMembersArgs = {
    medicalProfileId: string | null;
    relatedResourceId?: string;
    relatedResourceGroupIds?: string[];
    onCreateCareTeamMemberSuccess?: (careTeamMemberId: string) => void;
    onDeleteCareTeamMemberSuccess?: () => void;
    noCreateSuccessNotification?: boolean;
};

export type UseCareTeamMembersReturnValue = {
    createCareTeamMember: (body: APITypes.medicalProfileService.CreateCareTeamMemberRequest) => void;
    careTeamMembers: APITypes.medicalProfileService.CareTeamMemberDetails[];
    isLoadingCareTeamMembers: boolean;
    isCreatingCareTeamMember: boolean;
    isDeletingCareTeamMember: boolean;
    deleteCareTeamMember: (careTeamMemberId: string) => void;
    hasListPermission: boolean;
    isLoadingResourceRelatedCareTeamMembers: boolean;
    resourceRelatedCareTeamMembers: APITypes.medicalProfileService.CareTeamMemberDetails[];
    isLoadingResourceGroupRelatedCareTeamMembers: boolean;
    resourceGroupRelatedCareTeamMembers: APITypes.medicalProfileService.CareTeamMemberDetails[];
};

export default function useCareTeamMembers({
    medicalProfileId,
    onCreateCareTeamMemberSuccess,
    noCreateSuccessNotification,
    onDeleteCareTeamMemberSuccess,
    relatedResourceId,
    relatedResourceGroupIds,
}: UseCareTeamMembersArgs): UseCareTeamMembersReturnValue {
    const invalidateCareTeamMemberCache = onCareTeamUpdate.resetCache({ medicalProfileId: medicalProfileId! });
    const hasListPermission = useHasPermission(medicalProfileId, 'careTeam', 'view');
    const notify = useNotify();
    const careTeamMembersQuery = useQuery({
        queryKey: getProfileCareTeamMembersCacheKey(medicalProfileId!),
        queryFn: async () => {
            return await appAPI.get(urls.api.careTeamMembers.careTeamMembers(medicalProfileId!));
        },
        enabled: hasListPermission && !!medicalProfileId,
    });
    useQueryErrorNotify(careTeamMembersQuery, intl('careTeamMembersLoadFailure'));

    const relatedToResourceQuery = useQuery({
        queryKey: getResourceRelatedCareTeamMembersCacheKey(medicalProfileId!, relatedResourceId || ''),
        queryFn: async () => {
            return await appAPI.get(
                urls.api.careTeamMembers.resourceRelatedCareTeamMembers(medicalProfileId!, relatedResourceId || ''),
            );
        },
        enabled: !!relatedResourceId && hasListPermission && !!medicalProfileId,
    });
    useQueryErrorNotify(relatedToResourceQuery, intl('relatedToResourceFailure'));

    const relatedToResourceGroupQuery = useQuery({
        queryKey: getResourceRelatedCareTeamMembersCacheKey(medicalProfileId!, relatedResourceGroupIds || []),
        queryFn: async () => {
            return await appAPI.post(urls.api.careTeamMembers.resourceGroupRelatedCareTeamMembers(medicalProfileId!), {
                body: {
                    groupResourceIds: relatedResourceGroupIds,
                } as APITypes.medicalProfileService.ListResourceGroupRelatedCareTeamMembersRequest,
            });
        },
        enabled: !!relatedResourceGroupIds?.length && !!hasListPermission && !!medicalProfileId,
    });
    useQueryErrorNotify(relatedToResourceGroupQuery, intl('relatedToResourceFailure'));

    const createCareTeamMember = useMutation<
        APITypes.medicalProfileService.CreateCareTeamMemberResponse,
        any,
        APITypes.medicalProfileService.CreateCareTeamMemberRequest
    >({
        mutationFn: async (careTeamMember: APITypes.medicalProfileService.CreateCareTeamMemberRequest) => {
            if (!medicalProfileId) {
                throw new Error('medicalProfileId is required to create a care team member');
            }
            return (await appAPI.post(urls.api.careTeamMembers.careTeamMembers(medicalProfileId), {
                body: careTeamMember,
            })) as APITypes.medicalProfileService.CreateCareTeamMemberResponse;
        },
        onSuccess: (response) => {
            !noCreateSuccessNotification && notify('success', intl('createSuccess'));
            onCreateCareTeamMemberSuccess?.(response.careTeamMemberId);
            invalidateCareTeamMemberCache();
        },
        onError: () => {
            notify('error', intl('createFailure'));
        },
    });

    const deleteCareTeamMember = useMutation({
        mutationFn: async (careTeamMemberId: string) => {
            if (!medicalProfileId) {
                throw new Error('medicalProfileId is required to delete a care team member');
            }

            return await appAPI.del(urls.api.careTeamMembers.careTeamMember(medicalProfileId, careTeamMemberId));
        },
        onSuccess: () => {
            notify('success', intl('deleteSuccess'));
            onDeleteCareTeamMemberSuccess?.();
            invalidateCareTeamMemberCache();
        },
        onError: () => {
            notify('error', intl('createFailure'));
        },
    });

    return {
        careTeamMembers: careTeamMembersQuery.data?.careTeam || [],
        isLoadingCareTeamMembers: careTeamMembersQuery.isLoading,
        createCareTeamMember: createCareTeamMember.mutate,
        isCreatingCareTeamMember: createCareTeamMember.isPending,
        isDeletingCareTeamMember: deleteCareTeamMember.isPending,
        deleteCareTeamMember: deleteCareTeamMember.mutate,
        hasListPermission,
        isLoadingResourceRelatedCareTeamMembers: relatedToResourceQuery.isLoading,
        resourceRelatedCareTeamMembers: relatedToResourceQuery.data?.careTeam || [],
        isLoadingResourceGroupRelatedCareTeamMembers: relatedToResourceGroupQuery.isLoading,
        resourceGroupRelatedCareTeamMembers: relatedToResourceGroupQuery.data?.careTeam || [],
    };
}
